import { BuilderActions } from '../../../shared'
import { IBuilderComponent } from '../../base'
import type {
  ColorAdaptiveValue,
  ColorVariant,
  ResponsiveValue,
  TypographyValue,
  VariableSelectValue,
  VariableTextValue,
} from '../../types'
import {
  deepMerge,
  parseAlignmentFormat,
  parseResponsiveFormat,
  parseToVariableSelect,
  parseToVariableText,
  parseTypographyFormat,
} from '../../parser-utils'
import {
  formatColorVariantFormat,
  formatFabAlignmentFormat,
  formatFromVariableSelect,
  formatFromVariableText,
  formatResponsiveCornerRadiusFormat,
  formatTypographyFormat,
} from '../../format-utils'
import fabTemplate from './fab.template.json'

type FabCornerRadius = 'rounded' | 'circle' | 'square' | 'drop-br' | 'drop-bl' | 'drop-tr' | 'drop-tl'

export interface IFabComponentSettings {
  // Data settings
  'fab-primary-data-active': boolean
  'fab-primary-data-icon': VariableSelectValue
  'fab-primary-data-label': VariableTextValue
  'fab-primary-data-badge': VariableTextValue
  'fab-secondary-data-active': boolean
  'fab-secondary-data-icon': VariableSelectValue
  'fab-secondary-data-label': VariableTextValue
  'fab-secondary-data-badge': VariableTextValue

  // Design settings
  'fab-primary-design-color-variant': ColorAdaptiveValue<ColorVariant>
  'fab-secondary-design-color-variant': ColorAdaptiveValue<ColorVariant>
  'fab-primary-design-shadow': string
  'fab-secondary-design-shadow': string
  'fab-primary-design-shape': ResponsiveValue<FabCornerRadius>
  'fab-secondary-design-shape': ResponsiveValue<FabCornerRadius>
  'fab-design-h-align': ResponsiveValue<string>
  'fab-design-typography': {
    'fab-primary-design-typography': ResponsiveValue<TypographyValue>
    'fab-secondary-design-typography': ResponsiveValue<TypographyValue>
  }
  'fab-design-hover-effect'?: string // Hover effect: 'none' | 'underline' | 'lift' | 'scale' | 'dim' | 'skew' | 'rotate' | 'glow' | 'shimmer' | 'border' | 'squeeze' | 'blur' | 'neon' | 'flip' | 'bounce'

  // Actions
  'fab-primary-action-click': BuilderActions
  'fab-secondary-action-click': BuilderActions
}

export interface IFabComponent extends IBuilderComponent {
  componentId: 'fab'
  groupId: 'button'
  settings: IFabComponentSettings
}

export interface AiFabInput {
  // Primary FAB
  primaryIcon?: string // Format: "icon:add" or "icon:home"
  primaryLabel?: string[] // Format: ["text:Add"] or ["text:Create ", "cr:itemType"]
  primaryBadge?: string[] // Format: ["text:3"] or ["text:New"]
  primaryActive?: boolean
  primaryColor?: string // Format: "primary-40" or "primary-40 dark:secondary-30"
  primaryShadow?: string
  primaryShape?: string // Format: "drop-br" or "circle ta:drop-br"
  primaryTypography?: string // Format: "label-medium" or "label-medium ta:label-large"
  primaryClickAction?: BuilderActions[]

  // Secondary FAB
  secondaryIcon?: string // Format: "icon:edit" or "icon:delete"
  secondaryLabel?: string[] // Format: ["text:Edit"] or ["text:Update ", "cr:itemName"]
  secondaryBadge?: string[] // Format: ["text:1"] or ["text:Updated"]
  secondaryActive?: boolean
  secondaryColor?: string // Format: "primary-40" or "primary-40 dark:secondary-30"
  secondaryShadow?: string
  secondaryShape?: string // Format: "drop-br" or "circle ta:drop-br"
  secondaryTypography?: string // Format: "label-medium" or "label-medium ta:label-large"
  secondaryClickAction?: BuilderActions[]

  // Shared settings
  align?: string // Format: "h:end" or "h:end ta:h:center"
  hoverEffect?: string // Format: "bounce" or "underline" | 'none' | 'lift' | 'scale' | 'dim' | 'skew' | 'rotate' | 'glow' | 'shimmer' | 'border' | 'squeeze' | 'blur' | 'neon' | 'flip' | 'bounce'
}

const FAB_DEFAULT_TEMPLATE = fabTemplate as unknown as IFabComponentSettings

/**
 * Parse color variant format for FAB
 * Format: "primary-40" or "primary-40 dark:secondary-30"
 */
function parseColorVariantFormat(format: string): ColorAdaptiveValue<ColorVariant> {
  const parts = format.trim().split(/\s+/)
  const lightPart = parts[0] || 'primary-40'
  const darkPart = parts.find((p) => p.startsWith('dark:'))?.substring(5) || lightPart

  return {
    light: lightPart as ColorVariant,
    dark: darkPart as ColorVariant,
  }
}

/**
 * Parse FAB corner radius format string
 * Format: "drop-br" or "circle ta:drop-br"
 * Valid values: "rounded", "circle", "square", "drop-br", "drop-bl", "drop-tr", "drop-tl"
 */
function parseFabCornerRadiusFormat(format: string): ResponsiveValue<FabCornerRadius> {
  const validValues: FabCornerRadius[] = [
    'rounded',
    'circle',
    'square',
    'drop-br',
    'drop-bl',
    'drop-tr',
    'drop-tl',
  ]
  
  return parseResponsiveFormat(format, (value) => {
    // Validate and return the value, or fallback to 'circle' if invalid
    const normalized = value.trim().toLowerCase()
    if (validValues.includes(normalized as FabCornerRadius)) {
      return normalized as FabCornerRadius
    }
    // Fallback to 'circle' for invalid values
    return 'circle'
  })
}

/**
 * Parse horizontal alignment format for FAB
 * Format: "h:end" keeps the value as "end", not converting to "left/right"
 */
function parseFabAlignmentFormat(format: string): ResponsiveValue<string> {
  const alignmentParsed = parseAlignmentFormat(format)
  if (alignmentParsed.hAlign) {
    // Keep the ComponentAlignment values as-is (start, center, end)
    return alignmentParsed.hAlign as ResponsiveValue<string>
  }
  // Default to 'end'
  return { phone: 'end', 'phone-landscape': 'end', tablet: 'end', 'tablet-landscape': 'end', desktop: 'end', tv: 'end' }
}

export class FabComp {
  getTemplate(): IFabComponentSettings {
    return FAB_DEFAULT_TEMPLATE
  }

  /**
   * format FAB component settings to AI FAB input
   */
  format(settings: IFabComponentSettings): AiFabInput {
    const template = this.getTemplate()
    const result: AiFabInput = {}

    // Primary FAB data
    if (settings['fab-primary-data-active'] !== undefined) {
      result.primaryActive = settings['fab-primary-data-active']
    }
    if (settings['fab-primary-data-icon']) {
      const icon = formatFromVariableSelect(settings['fab-primary-data-icon'])
      if (icon) {
        result.primaryIcon = icon
      }
    }
    if (settings['fab-primary-data-label']) {
      const label = formatFromVariableText(settings['fab-primary-data-label'])
      if (label.length > 0 && label[0] !== 'text:') {
        result.primaryLabel = label
      }
    }
    if (settings['fab-primary-data-badge']) {
      const badge = formatFromVariableText(settings['fab-primary-data-badge'])
      if (badge.length > 0 && badge[0] !== 'text:') {
        result.primaryBadge = badge
      }
    }

    // Secondary FAB data
    if (settings['fab-secondary-data-active'] !== undefined) {
      result.secondaryActive = settings['fab-secondary-data-active']
    }
    if (settings['fab-secondary-data-icon']) {
      const icon = formatFromVariableSelect(settings['fab-secondary-data-icon'])
      if (icon) {
        result.secondaryIcon = icon
      }
    }
    if (settings['fab-secondary-data-label']) {
      const label = formatFromVariableText(settings['fab-secondary-data-label'])
      if (label.length > 0 && label[0] !== 'text:') {
        result.secondaryLabel = label
      }
    }
    if (settings['fab-secondary-data-badge']) {
      const badge = formatFromVariableText(settings['fab-secondary-data-badge'])
      if (badge.length > 0 && badge[0] !== 'text:') {
        result.secondaryBadge = badge
      }
    }

    // Primary FAB design
    const primaryColor = formatColorVariantFormat(settings['fab-primary-design-color-variant'])
    if (primaryColor) {
      result.primaryColor = primaryColor
    }
    if (settings['fab-primary-design-shadow'] && settings['fab-primary-design-shadow'] !== template['fab-primary-design-shadow']) {
      result.primaryShadow = settings['fab-primary-design-shadow']
    }
    const primaryShape = formatResponsiveCornerRadiusFormat(settings['fab-primary-design-shape'])
    if (primaryShape) {
      result.primaryShape = primaryShape
    }
    const primaryTypography = formatTypographyFormat(
      settings['fab-design-typography']?.['fab-primary-design-typography']
    )
    if (primaryTypography) {
      result.primaryTypography = primaryTypography
    }

    // Secondary FAB design
    const secondaryColor = formatColorVariantFormat(settings['fab-secondary-design-color-variant'])
    if (secondaryColor) {
      result.secondaryColor = secondaryColor
    }
    if (settings['fab-secondary-design-shadow'] && settings['fab-secondary-design-shadow'] !== template['fab-secondary-design-shadow']) {
      result.secondaryShadow = settings['fab-secondary-design-shadow']
    }
    const secondaryShape = formatResponsiveCornerRadiusFormat(settings['fab-secondary-design-shape'])
    if (secondaryShape) {
      result.secondaryShape = secondaryShape
    }
    const secondaryTypography = formatTypographyFormat(
      settings['fab-design-typography']?.['fab-secondary-design-typography']
    )
    if (secondaryTypography) {
      result.secondaryTypography = secondaryTypography
    }

    // Shared design settings
    const align = formatFabAlignmentFormat(settings['fab-design-h-align'])
    if (align) {
      result.align = align
    }

    // Hover Effect
    if (settings['fab-design-hover-effect']) {
      const hoverEffect = settings['fab-design-hover-effect']
      if (hoverEffect && hoverEffect !== 'none') {
        result.hoverEffect = hoverEffect
      }
    }

    // Actions (copied as-is)
    if (settings['fab-primary-action-click']) {
      result.primaryClickAction = settings['fab-primary-action-click'] as any
    }
    if (settings['fab-secondary-action-click']) {
      result.secondaryClickAction = settings['fab-secondary-action-click'] as any
    }

    return result
  }

  /**
   * parse AI FAB input to FAB component settings
   */
  parse(input: AiFabInput): IFabComponentSettings {
    const result = deepMerge(FAB_DEFAULT_TEMPLATE, {})

    // Primary FAB data
    if (input.primaryActive !== undefined) {
      result['fab-primary-data-active'] = input.primaryActive
    }
    if (input.primaryIcon) {
      result['fab-primary-data-icon'] = parseToVariableSelect(input.primaryIcon)
    }
    if (input.primaryLabel) {
      result['fab-primary-data-label'] = parseToVariableText(input.primaryLabel)
    }
    if (input.primaryBadge) {
      result['fab-primary-data-badge'] = parseToVariableText(input.primaryBadge)
    }

    // Secondary FAB data
    if (input.secondaryActive !== undefined) {
      result['fab-secondary-data-active'] = input.secondaryActive
    }
    if (input.secondaryIcon) {
      result['fab-secondary-data-icon'] = parseToVariableSelect(input.secondaryIcon)
    }
    if (input.secondaryLabel) {
      result['fab-secondary-data-label'] = parseToVariableText(input.secondaryLabel)
    }
    if (input.secondaryBadge) {
      result['fab-secondary-data-badge'] = parseToVariableText(input.secondaryBadge)
    }

    // Primary FAB design
    if (input.primaryColor) {
      result['fab-primary-design-color-variant'] = parseColorVariantFormat(input.primaryColor)
    }
    if (input.primaryShadow) {
      result['fab-primary-design-shadow'] = input.primaryShadow
    }
    if (input.primaryShape) {
      result['fab-primary-design-shape'] = parseFabCornerRadiusFormat(
        input.primaryShape
      )
    }
    if (input.primaryTypography) {
      result['fab-design-typography']['fab-primary-design-typography'] = parseTypographyFormat(input.primaryTypography)
    }

    // Secondary FAB design
    if (input.secondaryColor) {
      result['fab-secondary-design-color-variant'] = parseColorVariantFormat(input.secondaryColor)
    }
    if (input.secondaryShadow) {
      result['fab-secondary-design-shadow'] = input.secondaryShadow
    }
    if (input.secondaryShape) {
      result['fab-secondary-design-shape'] = parseFabCornerRadiusFormat(
        input.secondaryShape
      )
    }
    if (input.secondaryTypography) {
      result['fab-design-typography']['fab-secondary-design-typography'] = parseTypographyFormat(
        input.secondaryTypography
      )
    }

    // Shared design settings
    if (input.align) {
      result['fab-design-h-align'] = parseFabAlignmentFormat(input.align)
    }

    // Hover Effect
    if (input.hoverEffect) {
      result['fab-design-hover-effect'] = input.hoverEffect
    }

    // Actions
    if (input.primaryClickAction) {
      // @ts-ignore
      result['fab-primary-action-click'] = input.primaryClickAction
    }
    if (input.secondaryClickAction) {
      // @ts-ignore
      result['fab-secondary-action-click'] = input.secondaryClickAction
    }

    return result
  }
}
